import React, { createContext, useState, useEffect, useCallback, useContext } from "react";

export interface CartItem {
  id: number;
  userId?: string; 
  productId: number;
  productName: string;
  quantity: number;
  price: number;
  imageUrl?: string;
  seller?: string;
}

interface CartContextType {
  cart: CartItem[];
  fetchCart: () => Promise<void>;
  updateQuantity: (productId: number, newQuantity: number) => Promise<void>;
  removeFromCart: (productId: number) => Promise<void>;
  addToCart: (
    product: {
      productId: number;
      productName: string;
      price: number;
      imageUrl?: string;
      seller?: string;
    },
    quantity: number
  ) => Promise<void>;
  clearLocalCart: () => void;
}

export const CartContext = createContext<CartContextType | undefined>(undefined);

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [cart, setCart] = useState<CartItem[]>([]);

  const getAuthHeader = (): Record<string, string> => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const isLoggedIn = () => !!localStorage.getItem("token");

  const fetchCart = useCallback(async () => {
    if (isLoggedIn()) {
      try {
        const response = await fetch("https://www.ayushvatika.com/api/cart", {
          headers: { ...getAuthHeader(), "Content-Type": "application/json" },
          credentials: "include",
        });
        if (response.ok) {
          const data: CartItem[] = await response.json();
          setCart(data);
        } else if (response.status === 204) {
          setCart([]);
        } else {
          setCart([]);
        }
      } catch (error) {
        console.error("Error fetching server cart:", error);
      }
    } else {
      const localCart = JSON.parse(localStorage.getItem("guest_cart") || "[]");
      setCart(localCart);
    }
  }, []);

  const addToCart = async (product: { productId: number; productName: string; price: number; imageUrl?: string; seller?: string }, quantity: number) => {
    if (isLoggedIn()) {
      try {
        const response = await fetch("https://www.ayushvatika.com/api/cart/add", {
          method: "POST",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          credentials: "include",
          body: JSON.stringify({
            productId: product.productId,
            productName: product.productName,
            price: product.price,
            quantity,
            imageUrl: product.imageUrl,
            seller: product.seller,
          }),
        });
        if (response.ok) {
          await fetchCart();
        }
      } catch (error) {
        console.error("Error adding to server cart:", error);
      }
    } else {
      const localCart: CartItem[] = JSON.parse(localStorage.getItem("guest_cart") || "[]");
      
      // ✅ UPDATED LOGIC: Match both Product ID and Product Name so Combos stay separate
      const existingItemIndex = localCart.findIndex(
        (item) => item.productId === product.productId && item.productName === product.productName
      );

      if (existingItemIndex > -1) {
        localCart[existingItemIndex].quantity += quantity;
      } else {
        localCart.push({
          id: Date.now(), 
          productId: product.productId,
          productName: product.productName,
          price: product.price,
          quantity: quantity,
          imageUrl: product.imageUrl,
          seller: product.seller,
        });
      }
      localStorage.setItem("guest_cart", JSON.stringify(localCart));
      setCart(localCart);
    }
  };

  const updateQuantity = async (productId: number, newQuantity: number) => {
    if (isLoggedIn()) {
      try {
        const response = await fetch("https://www.ayushvatika.com/api/cart/update", {
          method: "PUT",
          headers: { "Content-Type": "application/json", ...getAuthHeader() },
          credentials: "include",
          body: JSON.stringify({ productId, quantity: newQuantity }),
        });
        if (response.ok) await fetchCart();
      } catch (error) {
        console.error("Error updating server cart:", error);
      }
    } else {
      const localCart: CartItem[] = JSON.parse(localStorage.getItem("guest_cart") || "[]");
      const updatedCart = localCart.map(item => 
        item.productId === productId ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem("guest_cart", JSON.stringify(updatedCart));
      setCart(updatedCart);
    }
  };

  const removeFromCart = async (productId: number) => {
    if (isLoggedIn()) {
      try {
        const response = await fetch(`https://www.ayushvatika.com/api/cart/remove/${productId}`, {
          method: "DELETE",
          headers: { ...getAuthHeader() },
          credentials: "include",
        });
        if (response.ok) await fetchCart();
      } catch (error) {
        console.error("Error removing from server cart:", error);
      }
    } else {
      const localCart: CartItem[] = JSON.parse(localStorage.getItem("guest_cart") || "[]");
      const updatedCart = localCart.filter(item => item.productId !== productId);
      localStorage.setItem("guest_cart", JSON.stringify(updatedCart));
      setCart(updatedCart);
    }
  };

  const clearLocalCart = () => {
    localStorage.removeItem("guest_cart");
    setCart([]);
  };

  const syncGuestCartToServer = async () => {
    const localCart: CartItem[] = JSON.parse(localStorage.getItem("guest_cart") || "[]");
    if (localCart.length > 0 && isLoggedIn()) {
      for (const item of localCart) {
        await addToCart({
            productId: item.productId,
            productName: item.productName,
            price: item.price,
            imageUrl: item.imageUrl,
            seller: item.seller
        }, item.quantity);
      }
      localStorage.removeItem("guest_cart");
    }
    await fetchCart();
  };

  useEffect(() => {
    fetchCart();
    const handleAuthChange = async () => {
      if (isLoggedIn()) {
        await syncGuestCartToServer();
      } else {
        fetchCart();
      }
    };
    window.addEventListener("storage", handleAuthChange);
    window.addEventListener("loginSuccess", handleAuthChange); 
    return () => {
      window.removeEventListener("storage", handleAuthChange);
      window.removeEventListener("loginSuccess", handleAuthChange);
    };
  }, [fetchCart]);
  
  return (
    <CartContext.Provider value={{ cart, fetchCart, updateQuantity, removeFromCart, addToCart, clearLocalCart }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};